import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { passwordLogin, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Mail, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const [busy, setBusy] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const { setUser, refreshSettings, settings } = useAuth();
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/public/branding`)
      .then((r) => setBranding(r.data))
      .catch(() => setBranding(null));
  }, []);
  const brand = branding || settings || {};

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email & password required");
      return;
    }
    setBusy("password");
    try {
      const data = await passwordLogin(email, password);
      setUser(data.user);
      await refreshSettings();
      toast.success(`Welcome, ${data.user.name}`);
      navigate("/dashboard", { state: { user: data.user } });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex flex-col px-6 md:px-12 py-10">
        <div className="flex items-center gap-2 mb-12">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.company_name || "FieldOps"} className="h-10 w-10 rounded-sm object-cover" />
          ) : (
            <div className="h-10 w-10 rounded-sm bg-orange-500 flex items-center justify-center font-display font-extrabold text-zinc-950 text-xl">
              F
            </div>
          )}
          <div>
            <div className="font-display font-extrabold text-xl leading-none">
              {brand.company_name || "FieldOps"}
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">
              FSM · CRM Platform
            </div>
          </div>
        </div>
        <div className="max-w-md w-full mx-auto flex-1 flex flex-col justify-center">
          <div className="mb-1 text-[11px] font-bold tracking-[0.2em] uppercase text-orange-600">
            Welcome back
          </div>
          <h1 className="text-3xl md:text-4xl font-display font-extrabold tracking-tight text-zinc-950 mb-2">
            Run preventive & corrective maintenance like an engineering team.
          </h1>
          <p className="text-zinc-600 text-base mb-8 leading-relaxed">
            HVAC. Equipment. Civil Works. One operational hub for technicians,
            clients and managers &mdash; desktop and field-ready.
          </p>

          <Button
            data-testid="login-google-button"
            onClick={handleGoogle}
            className="w-full h-12 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-semibold text-base"
          >
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
              <path fill="#fff" d="M21.35 11.1H12v3.16h5.36c-.23 1.23-1.4 3.6-5.36 3.6-3.23 0-5.86-2.67-5.86-5.96s2.63-5.96 5.86-5.96c1.84 0 3.07.78 3.78 1.45l2.58-2.48C16.7 3.6 14.55 2.7 12 2.7 6.97 2.7 2.9 6.77 2.9 11.9S6.97 21.1 12 21.1c6.93 0 9.46-4.86 9.46-7.4 0-.5-.05-.87-.11-1.23z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="flex items-center my-6">
            <div className="flex-1 h-px bg-zinc-200" />
            <div className="px-3 text-[10px] tracking-[0.2em] uppercase text-zinc-500">
              Or sign in with email
            </div>
            <div className="flex-1 h-px bg-zinc-200" />
          </div>

          <form onSubmit={handlePassword} className="space-y-3" data-testid="password-login-form">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-11 rounded-sm"
                  placeholder="you@company.com"
                  autoComplete="email"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 h-11 rounded-sm"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
            </div>
            <Button
              type="submit"
              data-testid="login-password-submit"
              disabled={busy === "password"}
              className="w-full h-11 rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-bold"
            >
              {busy === "password" ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 text-xs text-zinc-500 leading-relaxed">
            By continuing you agree to FieldOps Terms. Authentication powered by
            Emergent&apos;s managed Google OAuth. First Google sign-in becomes
            admin; subsequent users default to client and can be promoted.
          </p>
          <div className="mt-4 pt-4 border-t border-zinc-200 text-center">
            <Link
              to="/request"
              data-testid="public-request-link"
              className="text-xs font-bold tracking-wider uppercase text-orange-600 hover:text-orange-700"
            >
              Need service? Submit a public request →
            </Link>
          </div>
        </div>
      </div>

      {/* Right: hero image (configurable via Settings.login_image_url) */}
      <div className="hidden lg:block relative bg-blueprint">
        <img
          src={
            brand.login_image_url ||
            "https://images.pexels.com/photos/6471911/pexels-photo-6471911.jpeg?w=1200"
          }
          alt="Field technician"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950/90 via-zinc-950/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="text-[10px] tracking-[0.3em] uppercase text-orange-400 mb-3">
            // Built for the field
          </div>
          <div className="font-display font-extrabold text-4xl leading-tight max-w-md">
            From rooftop chillers to parking-deck concrete &mdash; one platform that
            doesn&apos;t blink.
          </div>
          <div className="grid grid-cols-3 gap-6 mt-10 max-w-md">
            <Stat n="PM/CM" label="Auto Numbering" />
            <Stat n="3" label="Asset Classes" />
            <Stat n="AI" label="Triage & Reports" />
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ n, label }) => (
  <div>
    <div className="font-mono text-2xl font-bold text-orange-400">{n}</div>
    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300 mt-1">
      {label}
    </div>
  </div>
);
